import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  BUNDLES_TYPES,
  BundleSourceType,
  BundleTypeConfig,
  BuyAdsBundlesPayload,
  BuyOlxAdsBundlesPayload,
  OlxAdBundleItem,
  OlxAdsBundleData,
  OlxAdsBundleProduct,
  OlxVasDetails,
  PUSHUP_VAS_BUNDLE,
  SelectedAdsBundle,
} from './buy-olx-ads-bundles.interface';
import { PowerOlxV2Service } from '../../../../../modules/crm/power-olx-v2/services/power-olx-v2.service';
import { RequestHelper } from '../../../../../services/request-helper.service';
import { Subject, takeUntil } from 'rxjs';
import { BaseService } from '../../../../../services/base.service';
import { SelectionModel } from '@angular/cdk/collections';
import { DialogService } from '../../../../../services/dialog.service';
import {
  OlxAdvert,
  OlxAdvertItem,
  OlxPaidFeatureCode,
  PLANNER_OLX_ACTION,
  PLANNER_OLX_ACTIONS_REGISTRY,
  PowerOlxActionResult,
} from '../../../../../modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';
import { ModalService } from '../../../../../services/modal.service';
import {
  PLANNER_SCOPE,
  PLANNER_STATUS,
  PlannerCheckExisting,
  PlannerOlxPayload,
  PlannerSidebarData,
  PlannerSidebarMode,
} from '../planner-v2/planner-v2.interface';
import { ModalData } from '../../../../../model/modal-data.model';
import { SidebarData, SidebarType } from '../../stacked-sidebar.interface';
import { StackedSidebarComponent } from '../../stacked-sidebar.component';
import { PlannerService } from '../../../../../services/planner.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-buy-olx-ads-bundles',
  templateUrl: './buy-olx-ads-bundles.component.html',
  styleUrl: './buy-olx-ads-bundles.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BuyOlxAdsBundlesComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();

  @Input() payload!: BuyOlxAdsBundlesPayload;
  @Output() close = new EventEmitter<PowerOlxActionResult[]>();

  private channelId!: string;
  private selections = new Map<number, { bundle: SelectionModel<string>; vas: SelectionModel<string> }>();
  protected plannerId?: string;
  protected adverts!: OlxAdvertItem[];
  protected adsBundles!: OlxAdsBundleData;
  protected loaders = {
    ads_bundles: true,
    buy_ads_bundles: false,
  };
  protected totalPrice: number = 0;
  protected hasProducts: boolean = true;
  protected errorKey?: string;

  constructor(
    private readonly modalService: ModalService,
    private readonly dialogService: DialogService,
    private readonly powerOlxV2Service: PowerOlxV2Service,
    private readonly plannerService: PlannerService,
    private readonly requestHelper: RequestHelper,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
    protected readonly baseService: BaseService,
  ) {}

  ngOnInit(): void {
    this.initPayload();
    this.initAdsBundles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initPayload(): void {
    this.adverts = this.payload.adverts;
    this.channelId = this.payload.channel_id;
    this.plannerId = this.payload.planner_id;
  }

  private initAdsBundles(): void {
    const advertsIds = this.adverts.map((v) => v.id);

    this.requestHelper.handle<OlxAdsBundleData>(
      this.powerOlxV2Service.getAdsBundles(this.channelId, advertsIds),
      (adsBundles) => {
        this.errorKey = undefined;

        this.adsBundles = adsBundles;
        this.hasProducts = this.checkHasProducts(adsBundles);

        if (this.plannerId && !Array.isArray(this.adsBundles)) {
          this.adsBundles.vas_list.unshift(PUSHUP_VAS_BUNDLE as OlxVasDetails);
        }

        this.applyPreselectedCodes();

        if (!this.hasProducts) {
          this.totalPrice = 0;
        }
      },
      {
        key: 'power_olx_create_get_ads_bundles',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'ads_bundles',
        defaultErrorMessage: 'notifications_texts.error.failed_to_retrieve_paid_services',
        onError: (e) => {
          this.errorKey = 'buy_olx_ads_bundles.external_error';
          this.hasProducts = false;
          this.adsBundles = undefined as any;
          this.totalPrice = 0;
        },
      },
    );
  }

  private checkHasProducts(adsBundles: OlxAdsBundleData): boolean {
    if (Array.isArray(adsBundles)) {
      return adsBundles.some((ad) => ad.bundles?.length || ad.vas_list?.length);
    }

    return !!(adsBundles.bundles?.length || adsBundles.vas_list?.length);
  }

  private applyPreselectedCodes(): void {
    for (const advert of this.adverts) {
      if (!advert.codes?.length) continue;

      for (const code of advert.codes) {
        const adBundles = Array.isArray(this.adsBundles)
          ? this.adsBundles.find((b) => b.ad_id === advert.id)
          : this.adsBundles;

        if (!adBundles) continue;

        const isBundle = adBundles.bundles.some((b) => b.product_id === code);
        const isVas = adBundles.vas_list.some((v) => v.product_id === code);

        if (isBundle) {
          const selection = this.getSelection(advert.id, BundleSourceType.Bundle);
          selection.clear();
          selection.select(code);
        } else if (isVas) {
          const selection = this.getSelection(advert.id, BundleSourceType.Vas);
          selection.select(code);
        }
      }
    }

    this.updateTotalPrice();
  }

  protected selectAll(type: BundleTypeConfig): void {
    if (!Array.isArray(this.adsBundles)) return;

    for (const ad of this.adsBundles) {
      const product = this.getBundle(ad, type.product_id);
      if (product && product.enabled) {
        const selection = this.getSelection(ad.ad_id, type.source);
        selection.clear();
        selection.select(type.product_id);
      }
    }

    this.updateTotalPrice();
  }

  protected getBundle(ad: OlxAdBundleItem, productId: string): OlxAdsBundleProduct | null {
    const fromBundles = ad.bundles.find((b) => b.product_id === productId);
    const fromVas = ad.vas_list.find((v) => v.product_id === productId);
    return fromBundles || fromVas || null;
  }

  protected getAdvertById(adId: number): OlxAdvert | undefined {
    return this.adverts.find((a) => a.id === adId);
  }

  private getSelection(adId: number, source: BundleSourceType): SelectionModel<string> {
    if (!this.selections.has(adId)) {
      this.selections.set(adId, {
        bundle: new SelectionModel<string>(false),
        vas: new SelectionModel<string>(true),
      });
    }

    return this.selections.get(adId)![source];
  }

  protected isSelected(adId: number, productId: string, source: BundleSourceType): boolean {
    return this.getSelection(adId, source).isSelected(productId);
  }

  private getAllAds(): OlxAdBundleItem[] {
    if (Array.isArray(this.adsBundles)) return this.adsBundles;

    return [
      {
        ad_id: this.adverts[0].id,
        ad_title: this.adverts[0].title,
        bundles: this.adsBundles.bundles,
        vas_list: this.adsBundles.vas_list,
      },
    ];
  }

  protected getSingleAdStub(): OlxAdBundleItem {
    return {
      ad_id: this.adverts[0].id,
      ad_title: this.adverts[0].title,
      bundles: [],
      vas_list: [],
    };
  }

  private updateTotalPrice(): void {
    let total = 0;

    for (const ad of this.getAllAds()) {
      const selection = this.selections.get(ad.ad_id);
      if (!selection) continue;

      const selectedBundleId = selection.bundle.selected[0];
      const selectedVasIds = selection.vas.selected;

      const bundle = ad.bundles.find((b) => b.product_id === selectedBundleId);
      if (bundle) {
        total += bundle.pricing.regular.raw / 100;
      }

      for (const vasId of selectedVasIds) {
        const vas = ad.vas_list.find((v) => v.product_id === vasId);
        if (vas) {
          total += vas.pricing.regular.raw / 100;
        }
      }
    }

    this.totalPrice = total;
  }

  protected onToggle(ad: OlxAdBundleItem, item: OlxAdsBundleProduct, source: BundleSourceType): void {
    const selection = this.getSelection(ad.ad_id, source);
    const wasSelected = selection.isSelected(item.product_id);

    if (source === BundleSourceType.Bundle) {
      selection.clear();

      if (!wasSelected) {
        selection.select(item.product_id);
      }
    }

    if (source === BundleSourceType.Vas) {
      const selectedItems = [...selection.selected];

      const currentVasList = Array.isArray(this.adsBundles) ? ad.vas_list : this.adsBundles.vas_list;

      const clickedType = item.type;

      const filtered = selectedItems
        .map((pid) => currentVasList.find((v) => v.product_id === pid))
        .filter(Boolean)
        .filter((v) => v!.type !== clickedType)
        .map((v) => v!.product_id);

      selection.clear();

      for (const pid of filtered) {
        selection.select(pid);
      }

      if (!wasSelected) {
        selection.select(item.product_id);
      }
    }

    this.updateTotalPrice();
  }

  protected onBuyAdsBundles(): void {
    const selectedBundles = this.getSelectedBundles();
    if (!selectedBundles.length) return;

    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.pay_ad',
      price: this.totalPrice,
    });

    this.loaders.buy_ads_bundles = true;
    dialogRef.subscribe((result) => {
      this.loaders.buy_ads_bundles = false;

      if (!result) return;

      const payload: BuyAdsBundlesPayload = {
        selected_bundles: selectedBundles,
      };

      this.requestHelper.handle<PowerOlxActionResult[]>(
        this.powerOlxV2Service.buyAdsBundles(this.channelId, payload),
        (res) => this.onCloseSidebar(res),
        {
          key: 'power_olx_create_buy_ads_bundles',
          destroy$: this.destroy$,
          loaderRef: this.loaders,
          loaderKey: 'buy_ads_bundles',
          defaultErrorMessage: 'notifications_texts.error.failed_to_purchase_service',
        },
      );
    });
  }

  private buildPlannerPayloads(selectedBundles: SelectedAdsBundle[]): PlannerOlxPayload[] {
    return Object.values(
      selectedBundles.reduce(
        (acc, item) => {
          const key = item.advert_id;

          if (!acc[key]) {
            acc[key] = {
              channel_id: this.channelId,
              advert_id: item.advert_id,
              codes: [],
              advert: this.adverts.find((v) => v.id === item.advert_id),
            };
          }

          acc[key].codes!.push(item.code as OlxPaidFeatureCode);

          return acc;
        },
        {} as Record<number, PlannerOlxPayload>,
      ),
    );
  }

  protected onUpdatePlanner(): void {
    const selectedBundles = this.getSelectedBundles();
    if (!selectedBundles.length) return;

    const payloads = this.buildPlannerPayloads(selectedBundles);

    this.requestHelper.handle<void>(
      this.plannerService.updatePlannerPayload(this.plannerId!, payloads[0]),
      () => {
        this.notificationService.showSuccess(
          this.translate.instant('notifications_texts.success.success'),
          this.translate.instant('notifications_texts.success.successfully_updated'),
        );
        this.onCloseSidebar([]);
      },
      {
        key: 'power_olx_create_buy_ads_bundles',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'buy_ads_bundles',
        defaultErrorMessage: 'notifications_texts.error.failed_to_update',
      },
    );
  }

  private buildCheckExistingPlanners(payloads: PlannerOlxPayload[]): PlannerCheckExisting {
    return {
      active: true,
      status: PLANNER_STATUS.ACTIVE,
      action: PLANNER_OLX_ACTION.BUY_OLX_ADS_BUNDLES,
      payload: payloads.map((p) => ({
        channel_id: this.channelId,
        advert: { id: p.advert_id },
      })),
    };
  }

  protected onOpenPlanner(): void {
    const selectedBundles = this.getSelectedBundles();
    if (!selectedBundles.length) return;

    const payloads = this.buildPlannerPayloads(selectedBundles);
    const checkExistingPlanners = this.buildCheckExistingPlanners(payloads);

    const modalData: ModalData<SidebarData<PlannerSidebarData>> = {
      component: StackedSidebarComponent,
      width: '50vw',
      hideHeader: true,
      data: {
        type: SidebarType.Planner,
        payload: {
          mode: PlannerSidebarMode.Create,
          scope: PLANNER_SCOPE.OLX,
          actions: [PLANNER_OLX_ACTIONS_REGISTRY[PLANNER_OLX_ACTION.BUY_OLX_ADS_BUNDLES]],
          payloads,
          check_existing: checkExistingPlanners,
        },
      },
    };

    this.loaders.buy_ads_bundles = true;
    this.modalService
      .openModal(modalData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.loaders.buy_ads_bundles = false;

        if (res !== 'success') return;

        this.onCloseSidebar(res);
      });
  }

  private getSelectedBundles(): SelectedAdsBundle[] {
    const selected: SelectedAdsBundle[] = [];

    for (const ad of this.getAllAds()) {
      const selection = this.selections.get(ad.ad_id);
      if (!selection) continue;

      const bundleId = selection.bundle.selected[0];
      if (bundleId) selected.push({ advert_id: ad.ad_id, code: bundleId });

      for (const vasId of selection.vas.selected) {
        selected.push({ advert_id: ad.ad_id, code: vasId });
      }
    }

    return selected;
  }

  protected onCloseSidebar(status?: PowerOlxActionResult[]): void {
    this.close.emit(status);
  }

  protected onRetry(): void {
    this.errorKey = undefined;
    this.hasProducts = true;
    this.loaders.ads_bundles = true;
    this.initAdsBundles();
  }

  protected readonly Array = Array;
  protected readonly bundlesTypes = BUNDLES_TYPES;
  protected readonly BundleSourceType = BundleSourceType;
}
